import { useState } from 'react';

export interface SubmitState<Args extends unknown[]> {
  submit: (...args: Args) => Promise<boolean>;
  pending: boolean;
  error: Error | null;
  clearError: () => void;
}

/**
 * The write-side counterpart of useAsync: run an action from an event
 * handler, expose pending/error, report success as a boolean so the caller
 * decides whether to navigate. Errors are kept, not thrown — every write path
 * must render a 403 or 409 as a message, never a crash.
 */
export function useSubmit<Args extends unknown[]>(
  action: (...args: Args) => Promise<void>,
): SubmitState<Args> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submit = async (...args: Args): Promise<boolean> => {
    setPending(true);
    setError(null);
    try {
      await action(...args);
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      return false;
    } finally {
      setPending(false);
    }
  };

  return { submit, pending, error, clearError: () => setError(null) };
}
